import {
	Action,
	type DayOfWeek,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	formatScheduleTime,
	getStaffMemberDisplayName,
	type PaginationRequest,
	type Schedule,
	ScheduleType,
	scheduleToLabelInputs,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { CalendarClock, Filter, Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	fetchOrganizationSchedulesPaginatedQueryOptions,
	type ScheduleFilterParams,
} from "@/api/schedules";
import { fetchOrganizationStaffQueryOptions } from "@/api/staff";
import type { PaginationMeta } from "@/components/data-table";
import { DataTable, useDataTable } from "@/components/data-table";
import {
	ListPageCard,
	ListPageCardContent,
	ListPageCardHeader,
} from "@/components/ListPageCard";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useScheduleLabel } from "@/hooks/useScheduleLabel";

// All available schedule types
const ALL_TYPES: ScheduleType[] = [ScheduleType.GROUP, ScheduleType.INDIVIDUAL];

// Search params schema
const scheduleSearchSchema = z.object({
	page: z.number().min(1).catch(DEFAULT_PAGE),
	limit: z.number().min(1).max(100).catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
	type: z.nativeEnum(ScheduleType).optional().catch(undefined),
	isActive: z.boolean().optional().catch(undefined),
	enrolling: z.boolean().optional().catch(undefined),
	staffMemberId: z.string().optional().catch(undefined),
});

interface ScheduleListProps {
	organizationId: string;
	pagination: PaginationRequest;
	searchTerm?: string;
	filterParams?: ScheduleFilterParams;
	userId: string;
	token: string;
	canManageSchedules: boolean;
	onPaginationMetaChange?: (meta: PaginationMeta) => void;
}

function ScheduleList({
	organizationId,
	pagination,
	searchTerm,
	filterParams,
	userId,
	token,
	canManageSchedules,
	onPaginationMetaChange,
}: ScheduleListProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const formatScheduleLabel = useScheduleLabel();

	const {
		data: schedulesData,
		isLoading,
		error,
		paginationMeta,
	} = useDataTable<Schedule, ScheduleFilterParams>({
		queryOptions: fetchOrganizationSchedulesPaginatedQueryOptions,
		userId,
		token,
		organizationId,
		pagination,
		searchTerm,
		additionalParams: filterParams,
	});

	// Memoize pagination metadata to prevent unnecessary re-renders
	const memoizedPaginationMeta = useMemo(
		() => ({
			page: paginationMeta.page,
			limit: paginationMeta.limit,
			total: paginationMeta.total,
			totalPages: paginationMeta.totalPages,
			hasNextPage: paginationMeta.hasNextPage,
			hasPreviousPage: paginationMeta.hasPreviousPage,
		}),
		[
			paginationMeta.page,
			paginationMeta.limit,
			paginationMeta.total,
			paginationMeta.totalPages,
			paginationMeta.hasNextPage,
			paginationMeta.hasPreviousPage,
		],
	);

	// Pass pagination metadata up to parent
	useEffect(() => {
		if (onPaginationMetaChange) {
			onPaginationMetaChange(memoizedPaginationMeta);
		}
	}, [memoizedPaginationMeta, onPaginationMetaChange]);

	const handleRowClick = useCallback(
		(schedule: Schedule) => {
			navigate({
				to: "/app/org/$orgId/schedules/$scheduleId",
				params: { orgId: organizationId, scheduleId: schedule.id },
			});
		},
		[navigate, organizationId],
	);

	// Helper function to format days of week with translations
	const formatDaysOfWeekTranslated = useCallback(
		(daysOfWeek: DayOfWeek[]): string => {
			if (daysOfWeek.length === 0) return t("schedules.noDaysSelected");
			if (daysOfWeek.length === 7) return t("schedules.everyDay");

			const sortedDays = [...daysOfWeek].sort((a, b) => {
				const order = [
					"MONDAY",
					"TUESDAY",
					"WEDNESDAY",
					"THURSDAY",
					"FRIDAY",
					"SATURDAY",
					"SUNDAY",
				];
				return order.indexOf(a) - order.indexOf(b);
			});

			return sortedDays
				.map((day) => t(`schedules.form.options.days.${day.toLowerCase()}`))
				.join(", ");
		},
		[t],
	);

	const columns: ColumnDef<Schedule>[] = useMemo(() => {
		const cols: ColumnDef<Schedule>[] = [
			{
				accessorKey: "id",
				header: t("schedules.management.table.label"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="w-48 max-w-48">
							<div className="font-medium">
								{formatScheduleLabel(scheduleToLabelInputs(schedule))}
							</div>
							{schedule.description && (
								<div className="text-sm text-base-content/70">
									{schedule.description}
								</div>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "type",
				header: `${t("schedules.management.table.type")} / ${t("schedules.management.table.capacity")}`,
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="space-y-1">
							<div>
								<span className="badge badge-outline whitespace-nowrap">
									{schedule.type === ScheduleType.GROUP
										? t("schedules.form.options.type.group")
										: t("schedules.form.options.type.individual")}
								</span>
							</div>
							<div>
								<span className="badge badge-outline badge-info whitespace-nowrap">
									{t("schedules.management.table.maxCapacity", {
										count: schedule.capacity ?? 0,
									})}
								</span>
							</div>
							{(schedule.enrollmentCounts !== undefined ||
								schedule.enrollmentCount !== undefined) &&
								(() => {
									const active =
										schedule.enrollmentCounts?.active ??
										schedule.enrollmentCount ??
										0;
									const pending = schedule.enrollmentCounts?.pending ?? 0;
									const total =
										schedule.enrollmentCounts?.total ?? active + pending;

									// An active schedule with no enrollments is "Enrolling" — a
									// positive lifecycle state ("fill this class"), not an empty
									// count. Archived/empty schedules keep the neutral count badge.
									if (schedule.isActive && total === 0) {
										return (
											<div>
												<span
													className="badge badge-outline badge-info whitespace-nowrap"
													data-testid="schedule-status-enrolling"
												>
													{t("schedules.management.table.enrolling")}
												</span>
											</div>
										);
									}

									const parts: string[] = [];
									if (active > 0) {
										parts.push(
											t("schedules.management.filters.enrollmentsActive", {
												count: active,
											}),
										);
									}
									if (pending > 0) {
										parts.push(
											t("schedules.management.filters.enrollmentsPending", {
												count: pending,
											}),
										);
									}
									return (
										<div>
											<span className="badge badge-outline badge-secondary whitespace-nowrap">
												{parts.length > 0
													? parts.join(" · ")
													: `0 ${t("schedules.management.filters.enrollments")}`}
											</span>
										</div>
									);
								})()}
						</div>
					);
				},
			},
			{
				accessorKey: "establishment",
				header: t("schedules.management.table.establishment"),
				meta: { hiddenOnMobile: true },
				cell: ({ row }) => {
					const schedule = row.original;
					return schedule.establishment?.name || "Unknown";
				},
			},
			{
				accessorKey: "staffMember",
				header: t("schedules.management.table.staffMember"),
				meta: { hiddenOnMobile: true },
				cell: ({ row }) => {
					const schedule = row.original;
					return getStaffMemberDisplayName(schedule.staffMember);
				},
			},
			{
				accessorKey: "time",
				header: t("schedules.management.table.time"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="min-w-28">
							{formatScheduleTime(schedule.startTime, schedule.endTime)}
							<div className="font-medium">
								{schedule.recurrencyType === "SINGLE"
									? t("schedules.form.options.recurrency.single")
									: schedule.recurrencyType === "WEEKLY"
										? t("schedules.form.options.recurrency.weekly")
										: t("schedules.form.options.recurrency.monthly")}
							</div>
							{schedule.daysOfWeek.length > 0 && (
								<div className="text-sm text-base-content/70">
									{formatDaysOfWeekTranslated(schedule.daysOfWeek)}
								</div>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "isActive",
				header: t("schedules.management.table.status"),
				cell: ({ row }) => {
					const schedule = row.original;
					// Active recurring (WEEKLY) schedule with no upcoming sessions is a
					// data-integrity warning: it should have materialized sessions.
					const hasNoSessions =
						schedule.isActive &&
						schedule.recurrencyType === "WEEKLY" &&
						schedule.futureSessionCount === 0;
					return (
						<div className="space-y-1">
							<div>
								<span
									className={`badge badge-outline whitespace-nowrap ${
										schedule.isActive ? "badge-success" : "badge-warning"
									}`}
									data-testid={
										schedule.isActive
											? "schedule-status-active"
											: "schedule-status-archived"
									}
								>
									{schedule.isActive
										? t("schedules.management.table.active")
										: t("schedules.management.table.archived")}
								</span>
							</div>
							{hasNoSessions && (
								<div>
									<span
										className="badge badge-warning whitespace-nowrap"
										data-testid="schedule-status-no-sessions"
									>
										{t("schedules.management.table.noSessions")}
									</span>
								</div>
							)}
						</div>
					);
				},
			},
		];

		return cols;
	}, [t, formatDaysOfWeekTranslated, formatScheduleLabel]);

	// Mobile (< sm) rendering: one card per schedule instead of the squeezed
	// label/type/time/status columns. Same tap target as the desktop row click.
	const renderMobileCard = useCallback(
		(schedule: Schedule) => {
			const typeLabel =
				schedule.type === ScheduleType.GROUP
					? t("schedules.form.options.type.group")
					: t("schedules.form.options.type.individual");
			const capacityLabel = t("schedules.management.table.maxCapacity", {
				count: schedule.capacity ?? 0,
			});
			const recurrenceLabel =
				schedule.recurrencyType === "SINGLE"
					? t("schedules.form.options.recurrency.single")
					: schedule.recurrencyType === "WEEKLY"
						? t("schedules.form.options.recurrency.weekly")
						: t("schedules.form.options.recurrency.monthly");
			const active =
				schedule.enrollmentCounts?.active ?? schedule.enrollmentCount ?? 0;
			const pending = schedule.enrollmentCounts?.pending ?? 0;
			const total = schedule.enrollmentCounts?.total ?? active + pending;
			const isEnrolling = schedule.isActive && total === 0;
			const hasEnrollmentData =
				schedule.enrollmentCounts !== undefined ||
				schedule.enrollmentCount !== undefined;
			const enrollmentParts: string[] = [];
			if (active > 0) {
				enrollmentParts.push(
					t("schedules.management.filters.enrollmentsActive", {
						count: active,
					}),
				);
			}
			if (pending > 0) {
				enrollmentParts.push(
					t("schedules.management.filters.enrollmentsPending", {
						count: pending,
					}),
				);
			}
			const enrollmentLabel =
				enrollmentParts.length > 0
					? enrollmentParts.join(" · ")
					: `0 ${t("schedules.management.filters.enrollments")}`;
			const hasNoSessions =
				schedule.isActive &&
				schedule.recurrencyType === "WEEKLY" &&
				schedule.futureSessionCount === 0;
			return (
				<div className="p-3 space-y-2">
					<div className="flex items-start justify-between gap-2">
						<div className="min-w-0 flex-1">
							<div className="font-medium leading-tight line-clamp-2">
								{formatScheduleLabel(scheduleToLabelInputs(schedule))}
							</div>
							{schedule.description && (
								<div className="text-xs text-base-content/60 truncate mt-0.5">
									{schedule.description}
								</div>
							)}
						</div>
						<span
							className={`badge badge-outline badge-sm whitespace-nowrap shrink-0 ${
								schedule.isActive ? "badge-success" : "badge-warning"
							}`}
							data-testid={
								schedule.isActive
									? "schedule-status-active"
									: "schedule-status-archived"
							}
						>
							{schedule.isActive
								? t("schedules.management.table.active")
								: t("schedules.management.table.archived")}
						</span>
					</div>
					<div className="flex items-center justify-between gap-3">
						<span className="min-w-0 flex-1 truncate text-xs text-base-content/50">
							{typeLabel} · {capacityLabel}
						</span>
						<span className="font-mono text-sm text-base-content shrink-0">
							{formatScheduleTime(schedule.startTime, schedule.endTime)}
						</span>
					</div>
					<div className="flex items-center justify-between gap-2">
						<span className="min-w-0 flex-1 truncate text-xs text-base-content/50">
							{recurrenceLabel}
							{schedule.daysOfWeek.length > 0 &&
								` · ${formatDaysOfWeekTranslated(schedule.daysOfWeek)}`}
						</span>
						{(isEnrolling || hasEnrollmentData || hasNoSessions) && (
							<span className="flex items-center gap-1 shrink-0">
								{isEnrolling ? (
									<span
										className="badge badge-outline badge-info badge-sm whitespace-nowrap"
										data-testid="schedule-status-enrolling"
									>
										{t("schedules.management.table.enrolling")}
									</span>
								) : (
									hasEnrollmentData && (
										<span
											className="badge badge-outline badge-secondary badge-sm whitespace-nowrap"
											data-testid="schedule-enrollment-count"
										>
											{enrollmentLabel}
										</span>
									)
								)}
								{hasNoSessions && (
									<span
										className="badge badge-warning badge-sm whitespace-nowrap"
										data-testid="schedule-status-no-sessions"
									>
										{t("schedules.management.table.noSessions")}
									</span>
								)}
							</span>
						)}
					</div>
				</div>
			);
		},
		[t, formatDaysOfWeekTranslated, formatScheduleLabel],
	);

	return (
		<DataTable
			data={schedulesData}
			isLoading={isLoading}
			error={error}
			columns={columns}
			testId="schedules-table"
			canManage={canManageSchedules}
			onRowClick={canManageSchedules ? handleRowClick : undefined}
			renderMobileCard={renderMobileCard}
			emptyState={{
				icon: CalendarClock,
				title: t("schedules.management.empty.title"),
				description: t("schedules.management.empty.description"),
				createButton: canManageSchedules
					? {
							label: t("schedules.management.createFirst"),
							href: `/app/org/${organizationId}/schedules/new`,
						}
					: undefined,
			}}
		/>
	);
}

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/schedules/")({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		// Prefetch staff data for filter dropdown
		context.queryClient.ensureQueryData(
			fetchOrganizationStaffQueryOptions(token, params.orgId),
		);
		return { userId, token, orgId: params.orgId };
	},
	validateSearch: scheduleSearchSchema,
	component: SchedulesPage,
});

function SchedulesPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/schedules/",
	});
	const search = Route.useSearch();
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Permission check
	const can = useCan();
	const canManageSchedules = can(Action.MANAGE_SCHEDULES);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});

	// Search state
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Staff data for filter dropdown
	const { data: staff = [] } = useSuspenseQuery(
		fetchOrganizationStaffQueryOptions(token, orgId),
	);

	// Filter state
	const [selectedType, setSelectedType] = useState<ScheduleType | undefined>(
		search.type as ScheduleType | undefined,
	);
	const [selectedIsActive, setSelectedIsActive] = useState<boolean | undefined>(
		search.isActive,
	);
	const [selectedEnrolling, setSelectedEnrolling] = useState<
		boolean | undefined
	>(search.enrolling);
	const [selectedStaffMemberId, setSelectedStaffMemberId] = useState<
		string | undefined
	>(search.staffMemberId);

	// Build filter params
	const filterParams: ScheduleFilterParams = useMemo(
		() => ({
			type: selectedType,
			isActive: selectedIsActive,
			enrolling: selectedEnrolling,
			staffMemberId: selectedStaffMemberId,
		}),
		[selectedType, selectedIsActive, selectedEnrolling, selectedStaffMemberId],
	);

	// Debounce search
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (limit: number) => {
		setPageSize(limit);
		setCurrentPage(1);
	};

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
	};

	// Filter toggle handlers
	const handleToggleType = (type: ScheduleType) => {
		setSelectedType((prev) => (prev === type ? undefined : type));
		setCurrentPage(1);
	};

	// Active / Enrolling / Archived are one mutually-exclusive status choice:
	// selecting one clears the others (Enrolling is a subset of Active).
	const handleToggleIsActive = (isActive: boolean) => {
		setSelectedIsActive((prev) => (prev === isActive ? undefined : isActive));
		setSelectedEnrolling(undefined);
		setCurrentPage(1);
	};

	const handleToggleEnrolling = () => {
		setSelectedEnrolling((prev) => (prev ? undefined : true));
		setSelectedIsActive(undefined);
		setCurrentPage(1);
	};

	const handleToggleStaffMember = (staffMemberId: string) => {
		setSelectedStaffMemberId((prev) =>
			prev === staffMemberId ? undefined : staffMemberId,
		);
		setCurrentPage(1);
	};

	// Count active filters
	const activeFilterCount = useMemo(() => {
		let count = 0;
		if (selectedType) count++;
		if (selectedIsActive !== undefined) count++;
		if (selectedEnrolling) count++;
		if (selectedStaffMemberId) count++;
		return count;
	}, [
		selectedType,
		selectedIsActive,
		selectedEnrolling,
		selectedStaffMemberId,
	]);

	const hasActiveFilters = !!searchTerm || activeFilterCount > 0;

	const handleResetFilters = () => {
		setSearchTerm("");
		setSelectedType(undefined);
		setSelectedIsActive(undefined);
		setSelectedEnrolling(undefined);
		setSelectedStaffMemberId(undefined);
		setCurrentPage(1);
		navigate({
			to: "/app/org/$orgId/schedules",
			params: { orgId },
			search: { page: 1, limit: pageSize, search: "" },
		});
	};

	// Displayed count
	const displayedCount = useMemo(() => {
		if (!paginationMeta.total) return 0;
		const startIndex = (paginationMeta.page - 1) * paginationMeta.limit;
		const remaining = paginationMeta.total - startIndex;
		if (remaining <= 0) return 0;
		return Math.min(paginationMeta.limit, remaining);
	}, [paginationMeta.limit, paginationMeta.page, paginationMeta.total]);

	return (
		<div className="h-full flex flex-col space-y-2 sm:space-y-3">
			{/* Header */}
			<PageHeader
				title={t("schedules.management.title")}
				description={t("schedules.management.description")}
				showBackButton={false}
				actions={
					canManageSchedules ? (
						<Link
							to="/app/org/$orgId/schedules/new"
							params={{ orgId }}
							className="btn btn-primary btn-sm"
							data-testid="add-schedule-button"
							aria-label={t("schedules.management.createNew")}
						>
							<Plus className="w-4 h-4" />
							<span className="hidden sm:inline">
								{t("schedules.management.createNew")}
							</span>
						</Link>
					) : undefined
				}
			/>

			{/* Schedules Card */}
			<ListPageCard>
				<ListPageCardHeader className="space-y-3">
					{/* Search, filters and summary */}
					<div className="space-y-2 rounded-lg border border-base-200 bg-base-50 p-3">
						<div className="flex flex-row gap-3 w-full">
							{/* Search */}
							<div className="relative flex-1 sm:w-108 sm:flex-none">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
								<Input
									type="search"
									placeholder={t(
										"schedules.management.filters.searchPlaceholder",
									)}
									value={searchTerm}
									onChange={(e) => handleSearchChange(e.target.value)}
									className="pl-10 pr-10"
									data-testid="schedules-search-input"
									autoComplete="off"
									data-1p-ignore="true"
									data-lpignore="true"
									data-form-type="other"
								/>
								<div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
									{searchTerm && (
										<button
											type="button"
											onClick={() => handleSearchChange("")}
											className="text-base-content/40 hover:text-base-content/80 transition-colors"
											aria-label={t("common.clear")}
										>
											<X className="h-3 w-3" />
										</button>
									)}
									{searchTerm !== debouncedSearchTerm && (
										<div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary" />
									)}
								</div>
							</div>
							{/* Filter Popover */}
							<Popover>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										className="w-auto shrink-0 gap-2"
										data-testid="schedules-filter-button"
									>
										<Filter className="h-4 w-4" />
										{activeFilterCount > 0 && (
											<span className="badge badge-primary badge-xs">
												{activeFilterCount}
											</span>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-72 p-0" align="start">
									<div className="p-3 space-y-3">
										{/* Type Filter */}
										<div className="space-y-2">
											<span className="text-sm font-medium">
												{t("schedules.management.filters.filterByType")}
											</span>
											{ALL_TYPES.map((type) => (
												<button
													type="button"
													key={type}
													className="flex items-center space-x-2 px-1 py-1 hover:bg-base-200 rounded cursor-pointer w-full text-left"
													onClick={() => handleToggleType(type)}
												>
													<Checkbox
														id={`type-${type}`}
														checked={selectedType === type}
														onCheckedChange={() => handleToggleType(type)}
													/>
													<label
														htmlFor={`type-${type}`}
														className="text-sm cursor-pointer flex-1"
													>
														{type === ScheduleType.GROUP
															? t("schedules.management.filters.group")
															: t("schedules.management.filters.individual")}
													</label>
												</button>
											))}
										</div>

										<div className="border-t border-base-200" />

										{/* Status Filter */}
										<div className="space-y-2">
											<span className="text-sm font-medium">
												{t("schedules.management.filters.filterByStatus")}
											</span>
											<button
												type="button"
												className="flex items-center space-x-2 px-1 py-1 hover:bg-base-200 rounded cursor-pointer w-full text-left"
												onClick={() => handleToggleIsActive(true)}
											>
												<Checkbox
													id="status-active"
													checked={selectedIsActive === true}
													onCheckedChange={() => handleToggleIsActive(true)}
												/>
												<label
													htmlFor="status-active"
													className="text-sm cursor-pointer flex-1"
												>
													{t("schedules.management.filters.active")}
												</label>
											</button>
											<button
												type="button"
												className="flex items-center space-x-2 px-1 py-1 hover:bg-base-200 rounded cursor-pointer w-full text-left"
												onClick={handleToggleEnrolling}
											>
												<Checkbox
													id="status-enrolling"
													checked={selectedEnrolling === true}
													onCheckedChange={handleToggleEnrolling}
												/>
												<label
													htmlFor="status-enrolling"
													className="text-sm cursor-pointer flex-1"
												>
													{t("schedules.management.filters.enrolling")}
												</label>
											</button>
											<button
												type="button"
												className="flex items-center space-x-2 px-1 py-1 hover:bg-base-200 rounded cursor-pointer w-full text-left"
												onClick={() => handleToggleIsActive(false)}
											>
												<Checkbox
													id="status-inactive"
													checked={selectedIsActive === false}
													onCheckedChange={() => handleToggleIsActive(false)}
												/>
												<label
													htmlFor="status-inactive"
													className="text-sm cursor-pointer flex-1"
												>
													{t("schedules.management.filters.archived")}
												</label>
											</button>
										</div>

										<div className="border-t border-base-200" />

										{/* Staff Member Filter */}
										<div className="space-y-2">
											<span className="text-sm font-medium">
												{t("schedules.management.filters.filterByStaff")}
											</span>
											{staff.map((member) => (
												<button
													type="button"
													key={member.id}
													className="flex items-center space-x-2 px-1 py-1 hover:bg-base-200 rounded cursor-pointer w-full text-left"
													onClick={() => handleToggleStaffMember(member.id)}
												>
													<Checkbox
														id={`staff-${member.id}`}
														checked={selectedStaffMemberId === member.id}
														onCheckedChange={() =>
															handleToggleStaffMember(member.id)
														}
													/>
													<label
														htmlFor={`staff-${member.id}`}
														className="text-sm cursor-pointer flex-1"
													>
														{member.firstName && member.lastName
															? `${member.firstName} ${member.lastName}`
															: member.email}
													</label>
												</button>
											))}
										</div>
									</div>
								</PopoverContent>
							</Popover>
						</div>
						{/* Results summary */}
						<div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-base-content/60">
							<span className="whitespace-nowrap">
								{t("common.showingResults", {
									count: displayedCount,
									total: paginationMeta.total,
								})}
							</span>
							{searchTerm && (
								<>
									<span className="text-base-content/30">·</span>
									<span
										className="badge badge-ghost badge-sm max-w-50 truncate"
										title={searchTerm}
									>
										{t("common.search")}:
										<span className="ml-1 font-medium">{searchTerm}</span>
									</span>
								</>
							)}
							{hasActiveFilters && (
								<Button
									variant="ghost"
									size="sm"
									type="button"
									onClick={handleResetFilters}
									className="h-7 px-2 text-xs text-base-content/70 hover:text-base-content"
								>
									{t("common.clear")}
								</Button>
							)}
						</div>
					</div>
				</ListPageCardHeader>
				<ListPageCardContent>
					{/* Table */}
					<div className="flex-1 overflow-hidden">
						<ScheduleList
							organizationId={orgId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							searchTerm={debouncedSearchTerm}
							filterParams={filterParams}
							userId={userId}
							token={token}
							canManageSchedules={canManageSchedules}
							onPaginationMetaChange={handlePaginationMetaChange}
						/>
					</div>
					{/* Pagination */}
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</ListPageCardContent>
			</ListPageCard>
		</div>
	);
}
